import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';

import {
  calculateAverageRating,
  formatProviderLocation,
  formatProviderName,
  formatProviderPhone,
  formatShortTitle,
  getPublicBookingUrl,
} from '../../helpers/providers';
import useUser from '../../hooks/useUser';
import config from '../../config/config';
import { ScheduleWhiteIcon } from '../../styles/icons';
import './ProviderHeader.scss';
import BetaFeature from '../common/BetaFeature';
import CustomDialog from '../common/CustomDialog';
import BookingFromPublicProviderProfileForm from '../booking/BookingFromPublicProviderProfileForm';

const ProviderHeader = props => {
  const { provider, reviews, isMobile } = props;
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const navigate = useNavigate();
  const user = useUser();
  const isMediumScreen = useMediaQuery('@media (max-width: 1420px)')
  const averageRating = calculateAverageRating(reviews);
  const isXsmScreen = useMediaQuery(theme => theme.breakpoints.down('xsm'));
  console.log('USER', user);
  return (
    <Box className='ProviderHeaderComponent'>
      {/* Headers */}
      <Stack direction='row'>
        <Box className='ProviderHeader__left'>
          <Typography
            variant={isMobile ? 'h2' : 'title2'}
            lineHeight={isMobile ? '16px' : '35px'}
            color='text.primary'
            component='h2'
            title={formatProviderName(provider, { includeTitle: false })}
            marginBottom={{xs: '2px', sm: '6px'}}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              WebkitLineClamp: "2",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
            }}
          >
            {formatProviderName(provider, { includeTitle: false })}
          </Typography>
          <Typography
            variant={isMobile ? 'paragraphSmall' : 'title4'}
            color='text.light'
            component='p'
            marginBottom='10px'
          >
            {formatShortTitle(provider)}
          </Typography>
          {
            reviews && reviews.length
              ?
              <Box className='ProviderHeader__rating-box'>
                {
                  averageRating
                    ? <Rating
                      name="average-rating"
                      precision={0.5}
                      value={averageRating}
                      readOnly
                      sx={{
                        fontSize: '2.9rem',
                        color: config().colors.primary,
                        marginTop: '0.25em',
                        '& svg': {
                          width: '35px',
                        }
                      }}
                    />
                    : <Rating
                      name="average-rating"
                      value={null}
                      readOnly
                      sx={{
                        fontSize: '2.9rem',
                        color: config().colors.muted,
                        marginTop: '0.25em',
                        '& svg': {
                          width: '35px',
                        }
                      }}
                    />
                }
                <Typography
                  variant={isMobile ? 'paragraphSmall' : 'extraLargeInter'}
                  lineHeight={isMobile ? 'normal' : '1.375rem'}
                  letterSpacing='-0.03125rem'
                  color='text.gray'
                  component='p'
                  className='ProviderHeader__reviews'
                >
                  from{' '}
                  (<Typography
                    variant={isMobile ? 'paragraphSmall' : 'extraLargeInter'}
                    lineHeight={isMobile ? 'normal' : '1.375rem'}
                    letterSpacing='-0.03125rem'
                    color='text.gray'
                  >
                    {reviews.length} Reviews
                  </Typography>)
                </Typography>
              </Box>
              :
              <Box className='ProviderHeader__rating-box'>
                <Rating
                  name='average-rating'
                  value={null}
                  readOnly
                  size='large'
                  style={{ color: config().colors.muted }}
                />
                <Typography
                  variant={isMobile ? 'paragraphSmall' : 'extraLargeInter'}
                  lineHeight={isMobile ? 'normal' : '1.375rem'}
                  letterSpacing='-0.03125rem'
                  color='text.gray'
                  component='p'
                  className='ProviderHeader__reviews'
                >
                  No Reviews Yet
                </Typography>
              </Box>
          }

          <Box>
            <Button
              variant='primaryGradient'
              size={isMediumScreen ? 'xmedium' : 'xlarge'}
              disabled={props.disableRequestSession || props.isProvider}
              onClick={() => {
                if (user) {
                  if (user?.is_patient) {
                    navigate('/clients/booking?providerId=' + provider.id);
                  } else {
                    navigate(getPublicBookingUrl(provider));
                  }
                } else {
                  navigate(getPublicBookingUrl(provider));
                }
              }}
              endIcon={((!isXsmScreen && !isMobile) || isMobile) && <ScheduleWhiteIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Book Session
            </Button>
          </Box>
        </Box>

        <Stack justifyContent='space-between' className='ProviderHeader__right'>
          <Box className='ProviderHeader__location-box'>
            <Typography
              variant={isMobile ? 'paragraphSmall' : 'title4'}
              lineHeight='normal'
              letterSpacing='0.9px'
              className='ProviderHeader__location'
            >
              {formatProviderLocation(provider)}
            </Typography>
          </Box>
          <Box className='ProviderHeader__hourly-rate-box'>
            <Typography
              variant={isMobile ? 'h2' : 'title2'}
              color='text.primary'
              component='p'
              className='ProviderHeader__hourly-rate'
            >
              ${provider.hourlyRate}
            </Typography>
            <Typography
              variant={isMobile ? 'paragraphSmall' : 'title4'}
              color='text.light'
              component='p'
              className='ProviderHeader__hour'
            >
              /Hour
            </Typography>
          </Box>
          <Box className='ProviderHeader__phone-box'>
            <a
              // eslint-disable-next-line no-useless-escape
              href={`tel:${formatProviderPhone(provider).replace(/[\(\)-\s]/g, '')}`}
              style={{ textDecoration: 'none' }}
            >
              <Button>
                <Typography
                  variant={isMobile ? 'paragraphSmallInter' : 'title4Inter'}
                  color='text.primary'
                  sx={{
                    lineHeight: '1.375rem',
                    letterSpacing: '-0.03125rem',
                  }}
                  className='ProviderHeader__location'>
                  {formatProviderPhone(provider)}
                </Typography>
              </Button>
            </a>
          </Box>
        </Stack>

      </Stack>
      <CustomDialog 
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        title={`Book with ${formatProviderName(provider)}`}
        padding='0px'
        fullWidth
        maxWidth='xs'
      >
        <BookingFromPublicProviderProfileForm provider={provider} />
      </CustomDialog>
    </Box>
  );
};

export default ProviderHeader;
